import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import juna from "./images/junatrain.png";
import junaGo from "./images/junaGo.png";

interface SetTrainNumberProps {
  setTrainNumber: (number: number) => void;
}
interface TrainInfo {
  trainNumber: number;
  trainType: string;
}

const SetTrainNumber: React.FC<SetTrainNumberProps> = ({ setTrainNumber }) => {
  const [cookiesWindow, setCookiesWindow] = useState(false);
  const [previousTrains, setPreviousTrains] = useState<TrainInfo[]>([]);

  const [inputValue, setInputValue] = useState<number | "">("");
  const [inputValueHistory, setInputValueHistory] = useState<number | "">("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [formattedDate, setFormattedDate] = useState<string>("");

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setYear(currentYear);
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    const date = new Date();
    const weekdays = [
      "Sunnuntai",
      "Maanantai",
      "Tiistai",
      "Keskiviikko",
      "Torstai",
      "Perjantai",
      "Lauantai",
    ];
    const weekday = weekdays[date.getDay()];

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    setFormattedDate(`${weekday} ${day}.${month}.${year}`);
  }, []);

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    const checkConsentExpiry = () => {
      const storedConsent = localStorage.getItem("cookieConsent");
      if (!storedConsent) {
        return false; // Consent not found
      }

      const consentData = JSON.parse(storedConsent);
      const dateSaved = new Date(consentData.dateSaved);
      const now = new Date();
      const oneYearInMs = 365 * 24 * 60 * 60 * 1000; // 12 months in milliseconds

      // Check if the consent is older than 12 months
      return now.getTime() - dateSaved.getTime() <= oneYearInMs;
    };

    const isConsentValid = checkConsentExpiry();

    if (!isConsentValid) {
      localStorage.removeItem("cookieConsent");
      localStorage.removeItem("edellisetJunat");
      localStorage.removeItem("lastStations");

      setCookiesWindow(true);
    } else {
      setCookiesWindow(false);

      const storedTrains = localStorage.getItem("edellisetJunat");
      if (storedTrains) {
        const trainsArray = JSON.parse(storedTrains) as TrainInfo[];
        setPreviousTrains(trainsArray);
      }
    }

    scrollToTop();
  }, []);

  const handleButtonClick = () => {
    if (inputValue) {
      const trainNum = Number(inputValue);
      setTrainNumber(trainNum);
      navigate(`/${trainNum}`);
    }
  };

  const handleButtonHistoryClick = () => {
    if (inputValueHistory) {
      const trainNumHistory = Number(inputValueHistory);
      setTrainNumber(trainNumHistory);
      localStorage.setItem("lastHistoryTrain", JSON.stringify(trainNumHistory));
      navigate(`/history/${trainNumHistory}`);
    }
  };

  const handlePreviousTrainClick = (trainNum: number) => {
    setTrainNumber(trainNum);
    navigate(`/${trainNum}`);
  };

  const handleLongPress = (trainNum: number) => {
    // Show confirmation dialog
    const confirmRemoval = window.confirm(
      `Poistetaanko junanumero ${trainNum} listalta?`,
    );
    if (confirmRemoval) {
      // Remove train number from localStorage
      const updatedTrains = previousTrains.filter(
        (train) => train.trainNumber !== trainNum,
      );

      // Update localStorage with the filtered list
      localStorage.setItem("edellisetJunat", JSON.stringify(updatedTrains));

      // Update state with the new list
      setPreviousTrains(updatedTrains);

      // Optionally reload the page or navigate
      navigate(`/set-train-number`); // Navigate to home after removal
    }
  };

  const handleMouseDown = (trainNum: number) => {
    // Start the long press timer
    timerRef.current = setTimeout(() => handleLongPress(trainNum), 500);
  };

  const handleMouseUp = () => {
    // Clear the timer if mouse is released before the long press
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null; // Reset the ref
    }
  };

  const handleConsentSaveKaikki = () => {
    const consentData = {
      pakolliset: true,
      analytics: true,
      ads: true,
      personalization: true,
      dateSaved: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consentData));
    setCookiesWindow(false);
  };

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 200);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("lastHistoryTrain");

    if (stored) {
      const trainNumHistory = JSON.parse(stored);
      setInputValueHistory(trainNumHistory);
    }
  }, []);

  return (
    <div
      className="ms-5 me-5 mb-2 mt-1"
      style={{ color: "white", textAlign: "start", marginLeft: 20 }}
    >
      <div
        className="ms-3 mb-1"
        style={{
          position: "absolute",
          left: "-16px",
          top: 48,
          color: "#1e5a72",
          fontWeight: 700,
          fontSize: 32,
          writingMode: "vertical-rl",
        }}
      >
        {formattedDate}
      </div>

      {/* Kulkutiedot */}
      <div
        style={{
          marginLeft: 36,
          marginTop: 12,
          marginBottom: 4,
          fontSize: 12,
          lineHeight: 1.0,
        }}
      >
        <strong>Kulkutiedot / Aikaleimat </strong>
        <br /> tavarajunat, matkustajajunat, veturit
      </div>
      <div className="d-flex justify-content-start">
        <input
          type="number"
          style={{
            marginLeft: 32,
            width: "220px",
            borderRadius: 4,
            boxShadow: "2px 2px 25px rgba(0, 0, 0, 0.9)",
          }}
          value={inputValueHistory}
          placeholder="junanumero"
          onChange={(e) => setInputValueHistory(Number(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleButtonHistoryClick();
            }
          }}
          required
        />
        <button
          onClick={handleButtonHistoryClick}
          style={{
            marginLeft: "10px",
            padding: "5px 10px",
            backgroundColor: "#1e5a72",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            boxShadow: "12px 2px 25px rgba(0, 0, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={junaGo}
            alt="search"
            style={{
              width: "20px",
              height: "20px",
              objectFit: "contain",
              transform: "scale(1.2)",
            }}
          />
        </button>
      </div>

      {/* Matkustajajunat */}
      <h1
        style={{
          marginLeft: 36,
          marginTop: 52,
          marginBottom: 2,
          fontSize: 22,
          fontWeight: 900,
        }}
      >
        Matkustajajunat
      </h1>
      <div className="d-flex justify-content-start">
        <input
          type="number"
          style={{
            marginLeft: 32,
            width: "220px",
            borderRadius: 4,
            boxShadow: "2px 2px 25px rgba(0, 0, 0, 0.9)",
          }}
          value={inputValue}
          placeholder="junanumero"
          onChange={(e) => setInputValue(Number(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleButtonClick();
            }
          }}
          required
        />
        <button
          onClick={handleButtonClick}
          style={{
            marginLeft: "10px",
            padding: "5px 10px",
            backgroundColor: "#1e5a72",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            boxShadow: "12px 2px 25px rgba(0, 0, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={junaGo}
            alt="search"
            style={{
              width: "20px",
              height: "20px",
              objectFit: "contain",
              transform: "scale(1.2)",
            }}
          />
        </button>
      </div>

      {/* Lähtevät junat */}
      <div
        className="edelliset-junat"
        style={{
          display: "flex",
          flexWrap: "wrap",
          marginTop: "12px",
          marginLeft: "24px",
          // paddingBottom: "20px", ei tarvitse koska kaksi sivua ovat yhtä aikaa näkyvissä
        }}
      >
        {previousTrains.length > 0 ? (
          previousTrains.map(
            (
              train, // Map over each TrainInfo object
            ) => (
              <div
                key={train.trainNumber} // Use trainNumber as the key
                onClick={() => handlePreviousTrainClick(train.trainNumber)} // Add click handler
                onMouseDown={() => handleMouseDown(train.trainNumber)} // Start long press
                onMouseUp={handleMouseUp} // Clear timeout if mouse is released
                onTouchStart={() => handleMouseDown(train.trainNumber)} // Handle touch start
                onTouchEnd={handleMouseUp} // Handle touch end
                style={{
                  width: "fit-content",
                  height: "34px",
                  padding: 8,
                  backgroundColor: "rgba(30, 90, 114, 0.4)",
                  color: "lightgray",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "6px",
                  borderRadius: "2px",
                  border: "none",
                  cursor: "pointer", // Change cursor to pointer
                  boxShadow: "2px 2px 12px rgba(0, 0, 0, 0.6)",
                }}
              >
                {train.trainType} {train.trainNumber}{" "}
                {/* Display the train number */}
              </div>
            ),
          )
        ) : (
          <div></div>
        )}
      </div>
      {/* sivut yhdistetty, joten tätä ei tarvitse näkyä */}
      {/* <div className="lahtevat-junat" style={{ paddingBottom: 160 }}>
        <h1 style={{ marginLeft: 36, marginBottom: 10 }}>Lähtevät Junat</h1>
        <strong style={{ marginLeft: 36, marginBottom: 0 }}>Asema</strong>
        <br />
        <div
          onClick={() => navigate("/station-departures")}
          style={{
            marginLeft: 32,
            width: "160px",
            height: 32,
            borderRadius: 8,
            boxShadow: "2px 2px 25px rgba(0, 0, 0, 0.9)",
            backgroundColor: "white",
          }}
        />
      </div> */}
      <div
        style={{
          position: "absolute",
          top: 120,
          left: "52px",
          zIndex: 2,
          transform: isVisible ? "translateY(0)" : "translateY(100vh)", // Luo smooth animaation
          transition: "transform 1.2s ease-out", // Siirtymän kesto ja smooth efekti
        }}
      >
        <img src={juna} alt="train" style={{ height: 86 }} />
      </div>

      {/* Navigation Links */}
      {/* sivut yhdistetty joten ei tarvitse kahta kertaa näyttää
      <nav
        style={{
          padding: "4px",
          backgroundColor: "#1e5a72",
          color: "lightgray",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          textAlign: "center",
          fontFamily: "Arial, sans-serif",
          fontSize: 11,
          zIndex: 2,
        }}
      >
        <div
          className="d-flex justify-content-center"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: "800px",
            margin: "0 auto",
            gap: "15px",
          }}
        >
          <Link to="/set-train-number" className="nav-link">
            minunjunani.fi
          </Link>
          <Link to="/privacy-policy" className="nav-link">
            Tietosuojakäytäntö
          </Link>
          <Link to="/cookies" className="nav-link">
            Evästeet
          </Link>
        </div>
        <div className="">&copy; {year} minunjunani.fi</div>
        <a
          href="https://www.digitraffic.fi/kayttoehdot/"
          target="_blank"
          style={{ color: "darkgray" }}
        >
          Liikennetietojen lähde Fintraffic / digitraffic.fi, lisenssi CC 4.0 BY
        </a>
      </nav> */}
      {cookiesWindow && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "20%",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            zIndex: 9999,
            color: "black",
          }}
        >
          <div
            className=""
            style={{
              backgroundColor: "white",
              borderRadius: 12,
              position: "absolute",
              bottom: 60,
              margin: 20,
              padding: "10px 20px 20px 20px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)",
              textAlign: "start",
            }}
          >
            <h3 className="">Evästeet</h3>
            <p className="m-0">
              Sovelluksemme käyttää evästeitä ja vastaavia seurantatekniikoita
              käyttäjäkokemuksen parantamiseksi, sivuston käytön analysoimiseksi
              sekä personoitujen mainosten näyttämiseksi Googlen mainosten ja
              analytiikan avulla.
            </p>

            <div>
              <div className="mb-4">
                <p style={{ marginRight: "4px", margin: 0 }}>
                  Lisätietoja löydät{" "}
                </p>
                <Link
                  to="/privacy-policy"
                  className="nav-link"
                  style={{ color: "#1e5a72" }}
                >
                  Tietosuojakäytännöstömme
                </Link>
              </div>
              <div className="d-flex">
                <button
                  style={{
                    width: "fit-content",
                    height: 60,
                    padding: 8,
                    backgroundColor: "#1e5a72",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "2px 12px 2px 2px",
                    borderRadius: "5px",
                    border: "none",
                    cursor: "pointer", // Change cursor to pointer
                    boxShadow: "2px 2px 12px rgba(0, 0, 0, 0.6)",
                  }}
                  onClick={() => navigate("/cookies")}
                >
                  Evästeasetukset
                </button>
                <button
                  style={{
                    width: "fit-content",
                    height: 60,
                    padding: 4,
                    backgroundColor: "green",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "2px",
                    borderRadius: "5px",
                    border: "none",
                    cursor: "pointer", // Change cursor to pointer
                    boxShadow: "2px 2px 12px rgba(0, 0, 0, 0.6)",
                  }}
                  onClick={() => handleConsentSaveKaikki()}
                >
                  Hyväksy kaikki evästeet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetTrainNumber;
